import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ArrowUpRight, RefreshCw, ShieldCheck } from 'lucide-react'
import '@/styles/bulk.css'
import { InvoiceDownload } from '@/components/InvoiceDownload'

type Purpose = 'transactional' | 'subscribed_marketing'
type Application = {
  id: string
  status: string
  application: Record<string, unknown>
  decision_history: { reason?: string; status?: string; at: string }[]
}
type Overview = {
  low_credit_threshold?: number | null
  paid_eligible: boolean
  access_status: string | null
  access_reason: string | null
  available_credits: number
  reserved_credits: number
  consumed_credits: number
  purposes: Purpose[]
  limits: Record<string, number>
  terms_version: string
  domains: { id: string; name: string }[]
  approved_domains: {
    domain_id: string
    status: string
    purposes: Purpose[]
  }[]
  applications: Application[]
}
type Pack = {
  id: string
  name: string
  credits: number
  price_cents: number
  currency: string
}
type Job = {
  id: string
  state: string
  purpose: Purpose
  counts: Record<string, number>
  pause_reason: string | null
  expires_at: string
  created_at: string
}
type Row = Record<string, string | number | null>
const purposeName = (purpose: string) =>
  purpose === 'subscribed_marketing' ? 'Subscribed marketing' : 'Transactional'
const stateName = (state: string) => state.replace(/_/g, ' ')
const number = (value: number) => value.toLocaleString()
const money = (cents: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(
    cents / 100,
  )
export function bulkError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join('; ')
  if (detail && typeof detail === 'object' && 'message' in detail)
    return String(detail.message)
  return 'Could not complete this request. Please try again.'
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-2 text-sm font-medium">
      {label}
      {children}
    </label>
  )
}
const initial = {
  business_name: '',
  website: '',
  operational_contact: '',
  recipient_source: '',
  permission_process: '',
  sample_message: '',
  expected_monthly_volume: '',
  typical_batch_size: '',
  frequency: '',
  completion_expectations: '',
}

// CHECKPOINT: PRD-16 FR-16.1.2/FR-16.6.1 Evidence-first application, without collecting an audience list.
function ApplicationForm({
  overview,
  done,
}: {
  overview: Overview
  done: () => void
}) {
  const previous = overview.applications[0]
  const [form, setForm] = useState(
    () =>
      Object.fromEntries(
        Object.keys(initial).map((key) => [
          key,
          String(previous?.application[key] ?? ''),
        ]),
      ) as typeof initial,
  )
  const [domains, setDomains] = useState<string[]>(
    () => (previous?.application.domain_ids as string[]) || [],
  )
  const [purposes, setPurposes] = useState<Purpose[]>(
    () => (previous?.application.purposes as Purpose[]) || [],
  )
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setBusy(true)
    try {
      await api.post('/account/bulk/applications', {
        ...form,
        expected_monthly_volume: Number(form.expected_monthly_volume),
        typical_batch_size: Number(form.typical_batch_size),
        domain_ids: domains,
        purposes,
        terms_version: overview.terms_version,
      })
      done()
    } catch (error) {
      setError(bulkError(error))
    } finally {
      setBusy(false)
    }
  }
  return (
    <form onSubmit={submit} className="bulk-application space-y-6">
      <h2 className="text-xl font-semibold">
        {previous?.status === 'information_required'
          ? 'Update your application'
          : 'Apply for bulk access'}
      </h2>
      <p className="text-sm text-muted-foreground">
        Share your sending use case. Leave out customer lists and credentials.
      </p>
      <fieldset className="bulk-form-section">
        <legend>Application details</legend>
        <div className="grid gap-4 sm:grid-cols-2">
          {(
            [
              'business_name',
              'website',
              'operational_contact',
              'expected_monthly_volume',
              'typical_batch_size',
              'frequency',
              'completion_expectations',
            ] as const
          ).map((key) => (
            <Field
              key={key}
              label={
                {
                  business_name: 'Business or application name',
                  website: 'Website',
                  operational_contact: 'Operational contact email',
                  expected_monthly_volume: 'Expected recipients per month',
                  typical_batch_size: 'Typical recipients per job',
                  frequency: 'How often will you send?',
                  completion_expectations: 'When do jobs need to finish?',
                }[key]
              }
            >
              <Input
                required
                type={
                  key.includes('volume') || key.includes('size')
                    ? 'number'
                    : key === 'website'
                      ? 'url'
                      : key === 'operational_contact'
                        ? 'email'
                        : 'text'
                }
                min={1}
                maxLength={key === 'business_name' ? 200 : 500}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </Field>
          ))}
        </div>
      </fieldset>
      <fieldset className="bulk-form-section">
        <legend>Audience & content</legend>
        {(
          ['recipient_source', 'permission_process', 'sample_message'] as const
        ).map((key) => (
          <Field
            key={key}
            label={
              {
                recipient_source: 'Where do your recipients come from?',
                permission_process: 'How do recipients give permission?',
                sample_message: 'Example message',
              }[key]
            }
          >
            <Textarea
              required
              minLength={20}
              maxLength={key === 'sample_message' ? 10000 : 5000}
              rows={3}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            />
          </Field>
        ))}
      </fieldset>
      <div className="grid gap-6 sm:grid-cols-2">
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium">
            Requested sending domains
          </legend>
          {overview.domains.length === 0 ? (
            <p className="text-sm">
              <Link className="underline" to="/domains">
                Add an active domain
              </Link>{' '}
              before applying.
            </p>
          ) : (
            overview.domains.map((domain) => (
              <label className="flex gap-2 text-sm" key={domain.id}>
                <input
                  type="checkbox"
                  checked={domains.includes(domain.id)}
                  onChange={(e) =>
                    setDomains(
                      e.target.checked
                        ? [...domains, domain.id]
                        : domains.filter((id) => id !== domain.id),
                    )
                  }
                />
                {domain.name}
              </label>
            ))
          )}
        </fieldset>
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium">
            Requested purposes
          </legend>
          {(['transactional', 'subscribed_marketing'] as Purpose[]).map(
            (purpose) => (
              <label className="flex gap-2 text-sm" key={purpose}>
                <input
                  type="checkbox"
                  checked={purposes.includes(purpose)}
                  onChange={(e) =>
                    setPurposes(
                      e.target.checked
                        ? [...purposes, purpose]
                        : purposes.filter((value) => value !== purpose),
                    )
                  }
                />
                {purposeName(purpose)}
              </label>
            ),
          )}
        </fieldset>
      </div>
      <div className="space-y-3 border-t pt-4 text-sm leading-6">
        <details className="bulk-disclosure">
          <summary>Bulk sending terms</summary>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-muted-foreground">
            <li>
              Active paid access, approval and approved sending limits apply.
            </li>
            <li>
              Use permission-based audiences and respect unsubscribes. Purchased
              or scraped lists, unsolicited outreach, impersonation and harmful
              content are prohibited.
            </li>
            <li>
              Credits are separate from your normal allowance. Purchases are
              non-refundable.
            </li>
          </ul>
        </details>
        <label className="flex items-start gap-2">
          <input
            className="mt-1"
            type="checkbox"
            required
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          I accept the bulk sending terms, version {overview.terms_version}.
        </label>
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Button
        disabled={busy || !accepted || !domains.length || !purposes.length}
      >
        {busy ? 'Submitting…' : 'Submit application'}
      </Button>
    </form>
  )
}

// CHECKPOINT: PRD-16 FR-16.6.1/FR-16.2.7 Server-priced credits, non-refundable consent and historical visibility.
export function BulkSending() {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [packs, setPacks] = useState<Pack[]>([])
  const [tab, setTab] = useState('Access')
  const [error, setError] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [version, setVersion] = useState(0)
  const [consent, setConsent] = useState(false)
  const [selectedPackId, setSelectedPackId] = useState('')
  const selectedPack =
    packs.find((pack) => pack.id === selectedPackId) || packs[0]
  const [buying, setBuying] = useState(false)
  const refresh = () => setVersion((value) => value + 1)
  useEffect(() => {
    let active = true
    setError('')
    setLoading(true)
    api
      .get<Overview>('/account/bulk/overview')
      .then(async ({ data }) => {
        if (!active) return
        setOverview(data)
        setDisabled(false)
        setPacks([])
        if (data.paid_eligible && data.access_status === 'active') {
          const response = await api.get<{ packs: Pack[] }>(
            '/account/bulk/packs',
          )
          if (active) setPacks(response.data.packs)
        }
      })
      .catch((error) => {
        if (active) {
          setError(bulkError(error))
          setDisabled(
            (error as { response?: { data?: { detail?: { error?: string } } } })
              .response?.data?.detail?.error === 'BULK_UNAVAILABLE',
          )
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [version])
  const buy = async (pack: Pack) => {
    setBuying(true)
    setError('')
    try {
      const { data } = await api.post('/account/bulk/checkout', {
        pack_id: pack.id,
        accept_non_refundable: consent,
      })
      const url = new URL(data.checkout_url)
      if (url.protocol !== 'https:') throw new Error('Invalid checkout URL')
      window.location.assign(url.href)
    } catch (error) {
      setError(bulkError(error))
      setBuying(false)
    }
  }
  return (
    <section className="bulk-console space-y-7">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="pt-page-title">Bulk sending</h1>
        </div>
        <div role="group" aria-label="Bulk actions" className="flex flex-wrap items-center gap-2">
          {overview && !disabled && (
            <>
              {overview.access_status !== 'active' && (
                <Button
                  variant="outline"
                  onClick={() => setTab('Access')}
                  disabled={loading || (!overview.paid_eligible && !overview.applications.length)}
                  aria-describedby={!overview.paid_eligible ? 'bulk-subscription-note' : undefined}
                >
                  {overview.applications.length ? 'View application' : 'Apply for bulk access'}
                </Button>
              )}
              <Button
                onClick={() => setTab('Credits')}
                disabled={!overview.paid_eligible || overview.access_status !== 'active' || loading}
                aria-describedby={!overview.paid_eligible ? 'bulk-subscription-note' : undefined}
              >
                Buy credits
              </Button>
            </>
          )}
          <Button variant="outline" onClick={refresh} disabled={loading}>
            <RefreshCw aria-hidden="true" className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </header>
      {disabled ? (
        <p role="status" className="rounded-lg border p-6">
          Bulk sending is not available yet.
        </p>
      ) : (
        <>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          {loading && !overview && <p role="status">Loading bulk access…</p>}
          {overview && (
            <>
              {!overview.paid_eligible && (
                <p id="bulk-subscription-note" className="rounded-md border p-4 text-sm">
                  We couldn’t confirm a current paid subscription for this
                  account. Check your subscription to apply or buy credits.{' '}
                  <Link to="/billing" className="underline">
                    Check subscription
                  </Link>
                  .
                </p>
              )}
              {overview.access_status &&
                overview.access_status !== 'active' && (
                  <p role="status" className="rounded-md border p-4 text-sm">
                    Bulk access is {overview.access_status}.{' '}
                    {overview.access_reason}
                  </p>
                )}
              <dl className="bulk-wallet">
                {[
                  ['Available credits', overview.available_credits],
                  ['Reserved credits', overview.reserved_credits],
                  ['Used credits', overview.consumed_credits],
                ].map(([label, value]) => (
                  <div key={label} className="bulk-wallet-metric">
                    <dt className="text-sm text-muted-foreground">{label}</dt>
                    <dd className="mt-1 text-2xl font-semibold tabular-nums">
                      {number(Number(value))}
                    </dd>
                  </div>
                ))}
              </dl>
              {overview.low_credit_threshold != null &&
                overview.available_credits > 0 &&
                overview.available_credits <= overview.low_credit_threshold && (
                  <p role="status" className="text-sm">
                    Your available bulk credits are low.{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => setTab('Credits')}
                    >
                      View credit packs
                    </button>
                    .
                  </p>
                )}
              {overview.available_credits === 0 &&
                overview.access_status === 'active' && (
                  <p className="text-sm">
                    No available credits. Add credits before submitting another
                    job.
                  </p>
                )}
              <nav aria-label="Bulk sections" className="bulk-tabs">
                {[
                  'Access',
                  'Credits',
                  'Jobs',
                  'Purchases',
                  'Ledger',
                  'Suppressions',
                  'API setup',
                ].map((name) => (
                  <Button
                    key={name}
                    variant="ghost"
                    aria-current={tab === name ? 'page' : undefined}
                    onClick={() => setTab(name)}
                  >
                    {name}
                  </Button>
                ))}
              </nav>
              {tab === 'Access' && (
                <div className="bulk-access space-y-6">
                  {(overview.access_status === 'active' || overview.applications.length > 0) && <h2 className="text-xl font-semibold">
                    {overview.access_status === 'active'
                      ? 'Approved access'
                      : 'Application status'}
                  </h2>}
                  {!overview.paid_eligible && !overview.applications.length && overview.access_status !== 'active' && (
                    <div className="rounded-lg border p-5 space-y-3">
                      <h2 className="text-xl font-semibold">Apply for bulk access</h2>
                      <p className="text-sm text-muted-foreground">
                        Confirm your paid subscription to start an application.
                        Once approved, you can buy credits and send.
                      </p>
                      <Button asChild variant="outline">
                        <Link to="/billing">View billing</Link>
                      </Button>
                    </div>
                  )}
                  {overview.applications.map((application) => (
                    <div
                      key={application.id}
                      className="space-y-2 border-b pb-4 text-sm"
                    >
                      <p className="font-medium capitalize">
                        {stateName(application.status)}
                      </p>
                      {application.decision_history
                        .filter((item) => item.reason)
                        .map((item, index) => (
                          <p key={index}>{item.reason}</p>
                        ))}
                    </div>
                  ))}
                  {overview.access_status === 'active' && (
                    <>
                      <p className="flex flex-wrap items-center gap-2 text-sm">
                        <ShieldCheck
                          aria-hidden="true"
                          className="h-4 w-4 text-primary"
                        />{' '}
                        Approved purposes:{' '}
                        {overview.purposes.map(purposeName).join(', ')}.
                      </p>
                      <ul className="bulk-domains">
                        {overview.approved_domains.map((domain) => (
                          <li key={domain.domain_id}>
                            <span className="font-medium">
                              {overview.domains.find(
                                (item) => item.id === domain.domain_id,
                              )?.name || domain.domain_id}{' '}
                            </span>
                            <span
                              className="bulk-state"
                              data-state={domain.status}
                            >
                              {stateName(domain.status)}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {domain.purposes.map(purposeName).join(', ')}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <dl className="bulk-limits">
                        {Object.entries(overview.limits).map(([key, value]) => (
                          <div key={key}>
                            <dt className="text-muted-foreground capitalize">
                              {(
                                {
                                  max_recipients_per_job: 'Recipients per job',
                                  max_queued_recipients: 'Queued recipients',
                                  max_active_jobs: 'Active jobs',
                                  recipients_per_minute:
                                    'Recipients per minute',
                                  recipients_per_day: 'Recipients per day',
                                } as Record<string, string>
                              )[key] || stateName(key)}
                            </dt>
                            <dd>{number(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </>
                  )}
                  {overview.paid_eligible &&
                    overview.access_status !== 'active' &&
                    (!overview.applications.length ||
                      ['information_required', 'declined'].includes(
                        overview.applications[0].status,
                      )) && (
                      <ApplicationForm
                        key={overview.applications[0]?.id || 'new'}
                        overview={overview}
                        done={refresh}
                      />
                    )}
                </div>
              )}
              {tab === 'Credits' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold">Buy bulk credits</h2>
                  <p className="text-sm text-muted-foreground">
                    Prepaid. No monthly reset or routine expiry.
                  </p>
                  {overview.paid_eligible &&
                  overview.access_status === 'active' ? (
                    <>
                      {packs.length ? (
                        <div className="bulk-purchase-layout">
                          <fieldset className="bulk-pack-options">
                            <legend className="sr-only">
                              Choose a credit pack
                            </legend>
                            {packs.map((pack) => (
                              <label
                                key={pack.id}
                                className="bulk-pack-option"
                                data-selected={selectedPack?.id === pack.id}
                              >
                                <input
                                  type="radio"
                                  name="credit-pack"
                                  value={pack.id}
                                  checked={selectedPack?.id === pack.id}
                                  onChange={() => setSelectedPackId(pack.id)}
                                />
                                <span className="flex-1 font-medium tabular-nums">
                                  {number(pack.credits)} credits
                                </span>
                                <span className="text-sm text-muted-foreground tabular-nums">
                                  {money(pack.price_cents, pack.currency)}
                                </span>
                              </label>
                            ))}
                          </fieldset>
                          <div className="bulk-checkout">
                            <p className="text-sm text-muted-foreground">
                              Selected pack
                            </p>
                            <p className="mt-2 text-3xl font-semibold tabular-nums">
                              {money(
                                selectedPack.price_cents,
                                selectedPack.currency,
                              )}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Final taxes calculated at checkout.
                            </p>
                            <label className="my-6 flex items-start gap-3 text-sm leading-6">
                              <input
                                className="mt-1"
                                type="checkbox"
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                              />
                              I understand that bulk credit purchases are
                              non-refundable.
                            </label>
                            <Button
                              className="w-full"
                              disabled={!consent || buying || loading}
                              onClick={() => buy(selectedPack)}
                            >
                              {buying ? 'Opening checkout…' : 'Continue to checkout'}{' '}
                              <ArrowUpRight
                                aria-hidden="true"
                                className="ml-2 h-4 w-4"
                              />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm">
                          Credit packs are not available for purchase yet.
                        </p>
                      )}
                      <details className="bulk-disclosure">
                        <summary>How credits work</summary>
                        <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                          <p>
                            We use one credit per recipient when our mail server
                            accepts your email for sending. Check delivery status
                            to see what happens next.
                          </p>
                          <p>
                            You need a paid subscription and bulk approval to
                            use credits. You’ll see any tax at checkout. We add
                            credits after confirming your payment.
                          </p>
                        </div>
                      </details>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm">
                        {overview.paid_eligible
                          ? 'Apply for bulk access first. You can buy credits after approval.'
                          : 'Confirm your paid subscription, then apply for bulk access to buy credits.'}
                      </p>
                      {overview.paid_eligible ? (
                        <Button variant="outline" onClick={() => setTab('Access')}>
                          {overview.applications.length ? 'View application' : 'Apply for bulk access'}
                        </Button>
                      ) : (
                        <Button asChild variant="outline"><Link to="/billing">View billing</Link></Button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {tab === 'Jobs' && (
                <Jobs
                  key={version}
                  eligible={
                    overview.paid_eligible &&
                    overview.access_status === 'active'
                  }
                  changed={refresh}
                />
              )}
              {['Purchases', 'Ledger', 'Suppressions'].includes(tab) && (
                <History key={`${tab}-${version}`} kind={tab.toLowerCase()} />
              )}
              {tab === 'API setup' && (
                <div className="max-w-3xl space-y-4 text-sm leading-6">
                  <h2 className="text-xl font-semibold">
                    Connect your application
                  </h2>
                  <dl className="bulk-api-steps">
                    <div>
                      <dt>01 · Create a live key</dt>
                      <dd>
                        Enable <code>bulk:send</code>, <code>bulk:read</code>{' '}
                        and optionally <code>bulk:manage</code>. Domain
                        restrictions apply. Keep the key on your server.
                      </dd>
                    </div>
                    <div>
                      <dt>02 · Validate a payload</dt>
                      <dd>
                        <code>POST /api/v1/bulk/validate</code>
                        <p>
                          Checks your payload without reserving credits or
                          sending.
                        </p>
                      </dd>
                    </div>
                    <div>
                      <dt>03 · Submit a job</dt>
                      <dd>
                        <code>POST /api/v1/bulk/jobs</code>
                        <p>
                          Include an <code>Idempotency-Key</code> header. Paused
                          jobs need explicit resume and keep their original
                          expiry.
                        </p>
                      </dd>
                    </div>
                  </dl>
                  <p className="text-muted-foreground">
                    Existing keys need explicit bulk scopes. Bulk test-key
                    capture is not yet available.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/api-keys">Manage API keys</Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}

function Pager({
  offset,
  more,
  loading,
  change,
}: {
  offset: number
  more: boolean
  loading: boolean
  change: (value: number) => void
}) {
  return (
    <div className="flex gap-2">
      <Button
        variant="outline"
        disabled={!offset || loading}
        onClick={() => change(offset - 50)}
      >
        Previous
      </Button>
      <Button
        variant="outline"
        disabled={!more || loading}
        onClick={() => change(offset + 50)}
      >
        Next
      </Button>
    </div>
  )
}
// CHECKPOINT: PRD-16 FR-16.6.1 Paginated history retains separate money and credit fields.
function History({ kind }: { kind: string }) {
  const [offset, setOffset] = useState(0),
    [rows, setRows] = useState<Row[]>([]),
    [more, setMore] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('')
  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api
      .get(`/account/bulk/history?kind=${kind}&offset=${offset}`)
      .then(({ data }) => {
        if (active) {
          setRows(data.items)
          setMore(data.has_more)
        }
      })
      .catch((e) => {
        if (active) setError(bulkError(e))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [kind, offset])
  const columns =
    kind === 'purchases'
      ? ['created_at', 'credits', 'pack_price', 'status', 'invoice']
      : kind === 'ledger'
        ? [
            'created_at',
            'operation',
            'available_delta',
            'reserved_delta',
            'consumed_delta',
            'reason',
          ]
        : ['email', 'source', 'created_at']
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold capitalize">{kind}</h2>
      {error && <p role="alert">{error}</p>}
      {loading ? (
        <p role="status">Loading history…</p>
      ) : (
        !error && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column} className="border-b p-3 capitalize">
                      {column === 'pack_price'
                        ? 'Pack price before tax'
                        : stateName(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    {columns.map((column) => (
                      <td key={column} className="border-b p-3 break-words">
                        {column === 'invoice' ? (
                          typeof row.invoice_path === 'string' ? <InvoiceDownload path={row.invoice_path} /> : <PurchaseInvoice url={row.invoice_url} />
                        ) : column === 'pack_price' ? (
                          money(Number(row.amount_cents), String(row.currency))
                        ) : column === 'created_at' ? (
                          new Date(String(row[column])).toLocaleString()
                        ) : (
                          String(row[column] ?? '—')
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && (
              <p className="py-6 text-sm text-muted-foreground">
                No {kind} recorded.
              </p>
            )}
          </div>
        )
      )}
      <Pager
        offset={offset}
        more={more && !error}
        loading={loading}
        change={setOffset}
      />
    </div>
  )
}

// CHECKPOINT: PRD-16 FR-16.3.5/FR-16.3.6/FR-16.6.1 Explicit controls and distinct submission/delivery outcomes.
function Jobs({
  eligible,
  changed,
}: {
  eligible: boolean
  changed: () => void
}) {
  const [offset, setOffset] = useState(0),
    [jobs, setJobs] = useState<Job[]>([]),
    [more, setMore] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [selected, setSelected] = useState<string | null>(null),
    [busy, setBusy] = useState(false)
  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api
      .get(`/account/bulk/jobs?offset=${offset}`)
      .then(({ data }) => {
        if (active) {
          setJobs(data.items)
          setMore(data.has_more)
        }
      })
      .catch((e) => {
        if (active) setError(bulkError(e))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [offset])
  const manage = async (job: Job, action: 'cancel' | 'resume') => {
    if (
      !window.confirm(
        action === 'cancel'
          ? 'Cancel the remaining emails? Emails already sending may still go through. We return credits for emails we confirm were not sent.'
          : 'Resume sending? We’ll check your access before continuing. The expiry date stays the same.',
      )
    )
      return
    setBusy(true)
    setError('')
    try {
      await api.post(`/account/bulk/jobs/${job.id}/${action}`)
      changed()
    } catch (e) {
      setError(bulkError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Bulk jobs</h2>
      {error && <p role="alert">{error}</p>}
      {loading ? (
        <p role="status">Loading jobs…</p>
      ) : (
        jobs.map((job) => (
          <article key={job.id} className="bulk-job space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="break-all font-mono text-sm">{job.id}</p>
              <span className="bulk-state" data-state={job.state}>
                {stateName(job.state)}
              </span>
            </div>
            <p className="text-sm capitalize">
              {purposeName(job.purpose)} · Expires{' '}
              {new Date(job.expires_at).toLocaleString()}
            </p>
            {job.pause_reason && (
              <p className="break-words text-sm">
                Pause reason: {job.pause_reason}
              </p>
            )}
            <dl className="bulk-job-counts">
              {Object.entries(job.counts).map(([state, count]) => (
                <div key={state}>
                  <dt>{stateName(state)}</dt>
                  <dd>{number(count)}</dd>
                </div>
              ))}
            </dl>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => setSelected(selected === job.id ? null : job.id)}
              >
                {selected === job.id ? 'Hide recipients' : 'View recipients'}
              </Button>
              {['queued', 'sending', 'paused'].includes(job.state) && (
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => manage(job, 'cancel')}
                >
                  Cancel unsent
                </Button>
              )}
              {job.state === 'paused' && (
                <Button
                  disabled={
                    !eligible || busy || new Date(job.expires_at) <= new Date()
                  }
                  onClick={() => manage(job, 'resume')}
                >
                  Resume job
                </Button>
              )}
            </div>
            {selected === job.id && <Recipients jobId={job.id} />}
          </article>
        ))
      )}
      {!loading && !error && !jobs.length && (
        <p className="py-6 text-sm">
          No bulk sends yet. Use the bulk API to send from your application.
        </p>
      )}
      <Pager
        offset={offset}
        more={more && !error}
        loading={loading}
        change={(value) => {
          setSelected(null)
          setOffset(value)
        }}
      />
    </div>
  )
}
function Recipients({ jobId }: { jobId: string }) {
  const [offset, setOffset] = useState(0),
    [rows, setRows] = useState<Row[]>([]),
    [more, setMore] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState('')
  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api
      .get(`/account/bulk/jobs/${jobId}/recipients?offset=${offset}`)
      .then(({ data }) => {
        if (active) {
          setRows(data.items)
          setMore(data.has_more)
        }
      })
      .catch((e) => {
        if (active) setError(bulkError(e))
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [jobId, offset])
  return (
    <div className="space-y-3">
      {error && <p role="alert">{error}</p>}
      {loading ? (
        <p role="status">Loading recipients…</p>
      ) : (
        !error && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr>
                  {[
                    'Recipient',
                    'Reference',
                    'Sending status',
                    'Delivery',
                    'Reason',
                  ].map((label) => (
                    <th key={label} className="border-b p-2">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    {[
                      'email',
                      'reference',
                      'submission_state',
                      'delivery_state',
                      'reason',
                    ].map((key) => (
                      <td key={key} className="border-b p-2">
                        {key === 'submission_state' || key === 'delivery_state'
                          ? stateName(String(row[key] ?? '—'))
                          : String(row[key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 0 && (
              <details className="bulk-disclosure mt-3">
                <summary>What do these statuses mean?</summary>
                <p className="mt-3 text-sm text-muted-foreground">
                  Accepted means our mail server has accepted your email for
                  sending. Check the delivery column for the recipient’s mail
                  server response.
                </p>
              </details>
            )}
            {rows.some((row) => row.submission_state === 'submission_uncertain') && (
              <p className="mt-3 text-sm text-muted-foreground">
                We couldn’t confirm whether some emails went through. Their
                credits stay reserved while we check. We won’t resend them
                automatically.
              </p>
            )}
          </div>
        )
      )}
      <Pager
        offset={offset}
        more={more && !error}
        loading={loading}
        change={setOffset}
      />
    </div>
  )
}

// CHECKPOINT: PRD-16 FR-16.6.1 Provider invoices are optional, private external documents.
export function PurchaseInvoice({ url }: { url: unknown }) {
  let safe = false
  if (typeof url === 'string' && !/[\s\\]/.test(url)) {
    try {
      const parsed = new URL(url)
      safe =
        parsed.protocol === 'https:' &&
        !parsed.username &&
        !parsed.password &&
        !parsed.hash
    } catch {
      /* An unavailable document never becomes a clickable link. */
    }
  }
  return safe ? (
    <a
      href={String(url)}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      className="whitespace-nowrap font-medium text-primary underline"
    >
      View invoice
    </a>
  ) : (
    <span className="text-muted-foreground">Not available yet</span>
  )
}
